/**
 * =============================================================================
 * Componente: MonthlyGoalRing
 * Versão: 1.1.0 (Suporte a filtro de período)
 *
 * Descrição:
 *   Card visual do progresso para o afiliado. Anel SVG com a quantidade de
 *   conversões/indicações + 3 micro-stats embaixo.
 *
 * Mudanças v1.1.0:
 *   - Aceita opcionalmente `params` (período) e um `label` para o cabeçalho.
 *     Quando há período custom, o título passa a ser "Progresso do período".
 * =============================================================================
 */
import { CalendarDays, Target, TrendingUp, Award } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useAffiliateMonthlyProgress,
} from '@/hooks/useAffiliateMonthlyProgress';
import type { UseAffiliateHomeOverviewParams } from '@/hooks/useAffiliateHomeOverview';
import { KpiInfoTooltip } from '@/components/affiliate/shared/KpiInfoTooltip';
import { cn } from '@/lib/utils';

function fmtBRL(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function currentMonthLabel(): string {
  const now = new Date();
  const m = now.toLocaleString('pt-BR', { month: 'long' });
  return `${m.charAt(0).toUpperCase() + m.slice(1)}`;
}

interface RingProps {
  conversions: number;
  referrals: number;
}

/**
 * Anel circular SVG: porção preenchida = taxa de conversão do mês.
 * Estilo "stroke" com gradiente do tema.
 */
function ProgressRing({ conversions, referrals }: RingProps) {
  const size = 132;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = referrals > 0 ? Math.min(conversions / referrals, 1) : 0;
  const dashOffset = circumference * (1 - ratio);
  const percent = Math.round(ratio * 100);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="aff-ring-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--aff-primary))" />
            <stop offset="100%" stopColor="hsl(var(--aff-accent))" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="hsl(var(--aff-surface-2))"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="url(#aff-ring-grad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          fill="none"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[28px] font-bold leading-none tabular-nums text-[hsl(var(--aff-foreground))]">
          {percent}%
        </span>
        <span className="mt-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-[hsl(var(--aff-muted-foreground))]">
          conversão
          <KpiInfoTooltip
            label="taxa de conversão"
            hint="Percentual de indicações criadas no período selecionado que foram convertidas (independente de quando a conversão ocorreu). Cálculo: conversões ÷ indicações × 100."
            iconSize={10}
          />
        </span>
      </div>
    </div>
  );
}

/** Métricas com drill-down disponível (v1.2.0). */
export type MonthlyGoalMetric = 'referrals' | 'conversions' | 'earned';

interface MonthlyGoalRingProps {
  /** Filtro de período (start/end). Sem args = mês corrente. */
  params?: UseAffiliateHomeOverviewParams;
  /** Texto exibido no canto direito do header (ex.: "Abril" ou "01/04 – 28/04"). */
  periodLabel?: string;
  /** Título do card (default: "Progresso do mês"). */
  title?: string;
  /** v1.2.0 — Callback de drill-down ao clicar em um indicador. */
  onMetricClick?: (metric: MonthlyGoalMetric) => void;
}

export function MonthlyGoalRing({ params, periodLabel, title, onMetricClick }: MonthlyGoalRingProps = {}) {
  const { data, isLoading, isError } = useAffiliateMonthlyProgress(params);

  return (
    <section aria-label="Progresso do período">
      <div className="aff-card p-5">
        {isLoading ? (
          <div className="flex items-center gap-5">
            <Skeleton className="h-[132px] w-[132px] rounded-full" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/5" />
            </div>
          </div>
        ) : isError || !data ? (
          <p className="py-6 text-center text-sm text-[hsl(var(--aff-muted-foreground))]">
            Não foi possível carregar o progresso.
          </p>
        ) : (
          <div className="flex flex-col sm:flex-row items-center gap-5">
            <ProgressRing
              conversions={data.conversionsThisMonth}
              referrals={data.referralsThisMonth}
            />
            <ul className="flex-1 w-full space-y-2.5">
              <Stat
                icon={<Target className="h-4 w-4" />}
                label="Indicações"
                value={String(data.referralsThisMonth)}
                tone="primary"
                hint="Quantidade de leads que chegaram até você no período selecionado. Conta pela data em que o lead foi cadastrado (criação da indicação)."
                onClick={onMetricClick ? () => onMetricClick('referrals') : undefined}
              />
              <Stat
                icon={<TrendingUp className="h-4 w-4" />}
                label="Conversões"
                value={String(data.conversionsThisMonth)}
                tone="accent"
                hint="Quantidade de indicações que viraram negócio fechado dentro do período selecionado. Conta pela data em que a conversão ocorreu, mesmo que o lead tenha sido criado em mês anterior."
                onClick={onMetricClick ? () => onMetricClick('conversions') : undefined}
              />
              <Stat
                icon={<Award className="h-4 w-4" />}
                label="Aprovado"
                value={fmtBRL(data.earnedThisMonth)}
                tone="success"
                hint="Soma das comissões aprovadas no período selecionado. Conta pela data de aprovação da comissão (não pela data da indicação). Inclui comissões já pagas."
                onClick={onMetricClick ? () => onMetricClick('earned') : undefined}
              />
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

interface StatProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone: 'primary' | 'accent' | 'success';
  /** Texto explicativo opcional exibido em tooltip ao lado do label. */
  hint?: string;
  /** v1.2.0 — Quando informado, a linha vira botão acessível (drill-down). */
  onClick?: () => void;
}

function Stat({ icon, label, value, tone, hint, onClick }: StatProps) {
  const toneClass = {
    primary: 'bg-[hsl(var(--aff-primary-soft))] text-[hsl(var(--aff-primary))]',
    accent: 'bg-[hsl(var(--aff-accent)/0.12)] text-[hsl(var(--aff-accent))]',
    success: 'bg-[hsl(var(--aff-success)/0.14)] text-[hsl(var(--aff-success))]',
  }[tone];

  // Conteúdo visual compartilhado entre a versão estática e a clicável.
  const content = (
    <>
      <span className={cn('flex h-9 w-9 items-center justify-center rounded-xl', toneClass)}>
        {icon}
      </span>
      <span className="flex-1 flex items-center gap-1 text-[12.5px] text-[hsl(var(--aff-muted-foreground))]">
        {label}
        {hint && <KpiInfoTooltip label={label} hint={hint} />}
      </span>
      <span className="text-[15px] font-bold tabular-nums text-[hsl(var(--aff-foreground))]">
        {value}
      </span>
    </>
  );

  if (!onClick) {
    return <li className="flex items-center gap-3">{content}</li>;
  }

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-label={`Ver indicações de ${label}`}
        className={cn(
          'group flex w-full items-center gap-3 rounded-xl px-1 py-0.5 text-left transition-colors',
          'hover:bg-[hsl(var(--aff-surface-2))] focus:outline-none',
          'focus-visible:ring-2 focus-visible:ring-[hsl(var(--aff-primary))]',
        )}
      >
        {content}
      </button>
    </li>
  );
}

